import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader, Panel, BrandButton } from "@/components/PageHeader";
import { SponsorDialog } from "@/components/sponsors/SponsorDialog";
import { SponsorRow, swap } from "@/components/sponsors/SponsorRow";
import { api, errMsg } from "@/lib/api";

export default function Sponsors() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, sponsor: null });
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get("/sponsors?all=true").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const move = (i, d) => {
    const next = swap(items, i, d);
    setItems(next);
    run(() => api.put("/sponsors/order", { ids: next.map((s) => s.id) }));
  };
  const toggle = (s) => run(() => api.put(`/sponsors/${s.id}`, { ...s, active: !s.active }), s.active ? "Hidden from homepage" : "Shown on homepage");
  const remove = (s) => setDeleting(s);
  return (
    <div data-testid="sponsors-page">
      <PageHeader eyebrow="Homepage" title="Sponsors" description="General sponsors shown in the scrolling logo strip on the homepage. Use the arrows to set the order. Event-specific sponsors are added in each event's setup."
        actions={<BrandButton onClick={() => setDialog({ open: true, sponsor: null })} data-testid="sponsor-add-button"><Plus className="w-4 h-4" />Add sponsor</BrandButton>} />
      <Panel title={`All sponsors${items ? ` (${items.length})` : ""}`} testId="sponsors-list-panel">
        {!items ? <div className="text-gray-500">Loading…</div> : items.length === 0
          ? <div className="text-gray-500 py-6 text-center" data-testid="sponsors-empty">No sponsors yet. The homepage strip stays hidden until you add one.</div>
          : <ul>{items.map((s, i) => <SponsorRow key={s.id} s={s} i={i} total={items.length} move={move} edit={(sp) => setDialog({ open: true, sponsor: sp })} toggle={toggle} remove={remove} />)}</ul>}
      </Panel>
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} title={`Delete ${deleting?.name || "sponsor"}?`} description="Remove this sponsor from the homepage sponsor list. This cannot be undone." confirmLabel="Delete sponsor" testId="sponsor-delete"
        onConfirm={async () => { await api.delete(`/sponsors/${deleting.id}`); toast.success("Sponsor deleted"); load(); }} />
      <SponsorDialog open={dialog.open} sponsor={dialog.sponsor} onClose={() => setDialog({ open: false, sponsor: null })} onSaved={load} />
    </div>
  );
}
